// Morning Readiness Score card — Release 2 preview, TEST USERS ONLY.
//
// Renders null entirely when `user.isTestUser` is not true so regular
// accounts see zero footprint. States:
//   - no_data: prompt to connect/sync a wearable
//   - building_baseline: "night N of 7"
//   - ready: gauge + per-component breakdown
//
// Pure presentation — readiness math lives in readinessScore.ts and is
// never used to mutate the canonical Menopause Impact Score.

import React from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import { useReadinessScore, MIN_BASELINE_NIGHTS, readinessBand, type ReadinessResult, type ReadinessSeriesPoint } from "./readinessScore";

const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const MUTED = "#6E4B71";
const LINE = "#E8D5DA";
const WH = "#ffffff";
const CREAM = "#FAF7F5";

function TestBadge() {
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 10,
      background: "#FFE4EA", color: PLUM, fontSize: 10, fontWeight: 600,
      letterSpacing: "0.05em", marginLeft: 8,
    }}>TEST PREVIEW</span>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: WH, border: `0.5px solid ${LINE}`, borderRadius: 18,
      padding: "16px 18px 14px", marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: ROSE, fontWeight: 600, letterSpacing: "0.08em" }}>
          MORNING READINESS
        </div>
        <TestBadge />
      </div>
      {children}
      <div style={{
        marginTop: 10, fontSize: 10, color: MUTED, lineHeight: 1.4,
        borderTop: `1px dashed ${LINE}`, paddingTop: 8,
      }}>
        Wellness signal only — not medical advice. Display-only preview;
        your Menopause Impact Score is unchanged.
      </div>
    </div>
  );
}

function ReadyView({ result }: { result: Extract<ReadinessResult, { state: "ready" }> }) {
  const band = readinessBand(result.readiness);
  const gR = 38, gCx = 50, gCy = 50;
  const angle = (result.readiness / 100) * Math.PI;
  const gx = gCx - gR * Math.cos(angle);
  const gy = gCy - gR * Math.sin(angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const arcPath = `M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 ${largeArc} 1 ${gx.toFixed(1)} ${gy.toFixed(1)}`;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <svg width={100} height={62} viewBox="0 0 100 62" aria-label={`Readiness ${result.readiness}`}>
          <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 1 1 ${gCx + gR} ${gCy}`} fill="none" stroke={CREAM} strokeWidth={6} strokeLinecap="round" />
          <path d={arcPath} fill="none" stroke={band.color} strokeWidth={6} strokeLinecap="round" />
          <text x={gCx} y={gCy - 2} textAnchor="middle" fontSize="22" fontWeight="600" fill={PLUM}>{result.readiness}</text>
          <text x={gCx} y={gCy + 12} textAnchor="middle" fontSize="9" fill={MUTED}>of 100</text>
        </svg>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 6 }}>
            <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, background: band.color + "22", color: band.color, fontSize: 11, fontWeight: 500 }}>{band.label}</span>
          </div>
          <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.45 }}>
            Based on last night ({result.lastNightDate}) vs. your last {result.baselineNightsCount}-night baseline.
          </div>
        </div>
      </div>
      <div className="km-metric-grid" style={{ marginTop: 10, gap: 6 }}>
        {result.components.map((c) => {
          const sign = c.delta > 0 ? "+" : c.delta < 0 ? "" : "±";
          return (
            <div key={c.key} className="km-metric-tile" style={{
              border: `0.5px solid ${LINE}`, borderRadius: 10, padding: "6px 8px",
              background: CREAM,
            }}>
              <div style={{ fontSize: 10.5, color: MUTED, fontWeight: 600, letterSpacing: "0.04em" }}>{c.label.toUpperCase()}</div>
              <div style={{ fontSize: 13, color: PLUM, fontWeight: 600 }}>
                {c.score} <span style={{ fontSize: 10, color: MUTED, fontWeight: 400 }}>/100</span>
              </div>
              <div style={{ fontSize: 10.5, color: MUTED }}>
                {c.lastNight}{c.unit} vs {c.baseline}{c.unit} ({sign}{c.delta}{c.unit})
              </div>
            </div>
          );
        })}
      </div>

    </>
  );
}

function HistoryChart({ series }: { series: ReadinessSeriesPoint[] }) {
  if (!series.length) return null;
  const hasAny = series.some((p) => p.score != null);
  const data = series.map((p) => {
    const [, m, d] = p.date.split("-");
    return { label: `${Number(m)}/${Number(d)}`, score: p.score };
  });
  const todayBand = series[series.length - 1]?.band
    ? readinessBand(series[series.length - 1]!.score as number)
    : { color: PLUM, label: "" as const };

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${LINE}` }}>
      <div style={{ fontSize: 11, color: PLUM, fontWeight: 600, marginBottom: 2 }}>Last 14 days</div>
      <div style={{ fontSize: 10, color: MUTED, marginBottom: 6 }}>
        Today is highlighted. Missing days = not enough baseline yet.
      </div>
      {!hasAny ? (
        <div style={{ fontSize: 10.5, color: MUTED, padding: "8px 0" }}>
          Not enough nights yet for a 14-day view.
        </div>
      ) : (
        <div style={{ width: "100%", height: 130 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 18, right: 10, left: -20, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: MUTED }} interval={1} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: MUTED }} ticks={[0, 50, 100]} />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: `0.5px solid ${LINE}` }}
                content={(props: any) => {
                  const { active, payload, label } = props;
                  if (!active || !payload || !payload.length) return null;
                  const first = payload.find((p: any) => p && p.value != null);
                  const val = first ? first.value : null;
                  return (
                    <div style={{ background: WH, fontSize: 11, borderRadius: 8, border: `0.5px solid ${LINE}`, padding: "6px 10px" }}>
                      <div style={{ color: MUTED, marginBottom: 2 }}>{label}</div>
                      <div style={{ color: PLUM }}>Readiness: {val == null ? "—" : val}</div>
                    </div>
                  );
                }}
              />
              <Line type="monotone" dataKey="score" stroke={PLUM} strokeOpacity={0.5} strokeWidth={1.5}
                    strokeDasharray="3 4" dot={false} activeDot={false} isAnimationActive={false}
                    connectNulls={true} legendType="none" />
              <Line type="monotone" dataKey="score" stroke={todayBand.color} strokeWidth={2}
                    dot={(props: any) => {
                      const { cx, cy, index, payload } = props;
                      if (payload?.score == null) return <g />;
                      const isToday = index === data.length - 1;
                      return <circle key={index} cx={cx} cy={cy} r={isToday ? 5 : 3}
                        fill={isToday ? todayBand.color : WH} stroke={todayBand.color}
                        strokeWidth={isToday ? 2 : 1.5} />;
                    }} connectNulls={false}>
                <LabelList dataKey="score" position="top" offset={8}
                  content={(props: any) => {
                    const { x, y, value, index } = props;
                    if (value == null) return null;
                    const n = data.length;
                    const anchor = index === 0 ? "start" : index === n - 1 ? "end" : "middle";
                    return <text x={x} y={y - 8} fontSize={9} fontWeight={500} fill={PLUM} textAnchor={anchor}>{Math.round(value)}</text>;
                  }}
                />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function ReadinessScoreCard({ user }: { user: { isTestUser?: boolean | null } | null }) {
  const isTest = user?.isTestUser === true;
  const { loading, enabled, result, series } = useReadinessScore(isTest);

  // HARD GATE — non-test users see nothing.
  if (!enabled) return null;

  if (loading) {
    return (
      <Frame>
        <div style={{ fontSize: 12, color: MUTED }}>Reading last night's signals…</div>
      </Frame>
    );
  }

  if (!result || result.state === "no_data") {
    return (
      <Frame>
        <div style={{ fontSize: 13, color: PLUM, fontWeight: 500, marginBottom: 4 }}>
          No wearable data yet
        </div>
        <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.45 }}>
          Connect a wearable and sync at least {MIN_BASELINE_NIGHTS + 1} nights to see your morning readiness.
        </div>
      </Frame>
    );
  }

  if (result.state === "building_baseline") {
    return (
      <Frame>
        <div style={{ fontSize: 13, color: PLUM, fontWeight: 500, marginBottom: 4 }}>
          Building baseline — night {result.baselineNightsCount} of {result.needed}
        </div>
        <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.45 }}>
          We need {Math.max(0, result.needed - result.baselineNightsCount)} more night{result.needed - result.baselineNightsCount === 1 ? "" : "s"} of sleep & recovery data to start comparing.
        </div>
        <HistoryChart series={series} />
      </Frame>
    );
  }

  return (
    <Frame>
      <ReadyView result={result} />
      <HistoryChart series={series} />
    </Frame>
  );
}
